import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import ProjectService, { Project } from '../../services/project.service';
import { NumberTextField } from '../../components/common';
import EmployeeService from '../../services/employee.service';

const ProjectForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [employees, setEmployees] = useState<any[]>([]);
  const [formData, setFormData] = useState<Project>({
    projectName: '',
    projectCode: '',
    manager: 0,
    status: 'planning',
    priority: 'medium',
    progress: 0,
  });

  useEffect(() => {
    fetchEmployees();
    if (isEdit && id) {
      fetchProject(parseInt(id));
    }
  }, [id, isEdit]);

  const fetchEmployees = async () => {
    try {
      const response = await EmployeeService.getAll();
      setEmployees(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchProject = async (projectId: number) => {
    try {
      const response = await ProjectService.getOne(projectId);
      const project = response.data?.data?.attributes;
      if (project) {
        setFormData({
          ...project,
          manager: project.manager?.data?.id || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching project:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isEdit && id) {
        await ProjectService.update(parseInt(id), formData);
      } else {
        await ProjectService.create(formData);
      }
      navigate('/projects');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit Project' : 'Create Project'}
      </Typography>

      <Paper sx={{ p: 3, mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                required
                label="Project Name"
                name="projectName"
                value={formData.projectName}
                onChange={handleChange}
              />
              <TextField
                fullWidth
                required
                label="Project Code"
                name="projectCode"
                value={formData.projectCode}
                onChange={handleChange}
                disabled={isEdit}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                select
                required
                label="Project Manager"
                name="manager"
                value={formData.manager}
                onChange={handleChange}
              >
                <MenuItem value={0}>Select Manager</MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.attributes?.firstName} {emp.attributes?.lastName}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                select
                label="Priority"
                name="priority"
                value={formData.priority || 'medium'}
                onChange={handleChange}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </TextField>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                label="Start Date"
                name="startDate"
                type="date"
                value={formData.startDate || ''}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="End Date"
                name="endDate"
                type="date"
                value={formData.endDate || ''}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <NumberTextField
                fullWidth
                label="Budget"
                name="budget"
                value={formData.budget || ''}
                onChange={handleChange}
                inputProps={{ step: '0.01', min: '0' }}
              />
              <TextField
                fullWidth
                select
                label="Status"
                name="status"
                value={formData.status || 'planning'}
                onChange={handleChange}
              >
                <MenuItem value="planning">Planning</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="onHold">On Hold</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </TextField>
            </Stack>

            <NumberTextField
              fullWidth
              label="Progress (%)"
              name="progress"
              value={formData.progress || 0}
              onChange={handleChange}
              inputProps={{ min: 0, max: 100 }}
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Description"
              name="description"
              value={formData.description || ''}
              onChange={handleChange}
            />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/projects')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default ProjectForm;

